/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { action } from '@ember/object';
import DS from 'ember-data';
import CustomerModel from 'cio/models/customer';
import { MergedAttributes, MergeSummary, ProfileType } from 'cio/utils/people-merge';
import AnalyticsService from 'cio/services/analytics';
import { queryDecode } from 'cio/helpers/query';
import { IdentifierKey } from 'cio/utils/identifiers';

interface MergeSelectCustomerArgs {
  profileType: ProfileType;
  primaryCustomer: CustomerModel | null;
  secondaryCustomer: CustomerModel | null;
  isLoadingMergedAttributes: boolean;
  mergedAttributes: MergedAttributes;
  mergeSummary: MergeSummary;
  searchTerm: string;
  onSelect: (customer: CustomerModel | null) => unknown;
}

const CUTOFF = 10;

export default class MergeSelectCustomer extends Component<MergeSelectCustomerArgs> {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  readonly ProfileType = ProfileType;

  @tracked customers: CustomerModel[] = [];
  @tracked isSelectingCustomer = false;

  @tracked temporaryCustomer: CustomerModel | null = null;

  get selectedCustomer() {
    switch (this.args.profileType) {
      case ProfileType.Primary:
      case ProfileType.Merged:
        return this.args.primaryCustomer;
      case ProfileType.Secondary:
        return this.args.secondaryCustomer;
      default:
        return null;
    }
  }

  searchTask = dropTask(async (opts: { id: string } | { email: string } | { filters: string | null }) => {
    let customers: CustomerModel[] = [];

    let analyticsData: Record<string, string | number | undefined> = {
      filterType: undefined,
      attributeName: undefined,
      attributeOperator: undefined,
      attributeValue: undefined,
      matchCount: 0,
    };

    if ('id' in opts) {
      let queryId = opts.id.trim();
      // Check if a record already exists in the store where any identifier
      // matches the given ID. If so, run `findRecord` with that record's
      // internal ID.
      // This is to prevent the Ember Data store from throwing errors. The API
      // responses will always return internal IDs for the record IDs, but will
      // match customers by any identifier.
      // If a customer model has an "id" indentifier of "10" but an internalId
      // of 0010123, and we run "findRecord('customer', 10)", Ember Data would
      // complain because of the ID mismatch.
      let existingMatchOnAnyIdentifier = this.store
        .peekAll('customer')
        .find((c) => Object.values(c.identifiers).some((id) => id === queryId));
      if (existingMatchOnAnyIdentifier?.identifiers?.[IdentifierKey.CioId] != null) {
        queryId = existingMatchOnAnyIdentifier.identifiers[IdentifierKey.CioId];
      }
      try {
        customers = [await this.store.findRecord('customer', queryId)];
      } catch (e) {
        // If the status is 404, it means that the findRecord simply didn't
        // match an existing customer. In this case, we need to clear the
        // customers array
        if (e?.errors?.[0]?.status === '404') {
          customers = [];
        } else {
          throw e;
        }
      }

      analyticsData.filterType = 'id';
    } else if ('email' in opts) {
      customers = (await this.store.query('customer', { email: opts.email.trim() })).toArray();

      analyticsData.filterType = 'email';
    } else {
      customers = (await this.store.query('customer', opts)).toArray();

      let decodedFilters = queryDecode(opts.filters ?? '');

      if (Array.isArray(decodedFilters)) {
        let [decodedFilter] = decodedFilters;
        analyticsData.filterType = 'condition';
        analyticsData.attributeName = decodedFilter.field;
        analyticsData.attributeOperator = decodedFilter.operator;
        analyticsData.attributeValue = decodedFilter.value ?? undefined;
      }
    }

    let customersWithoutSelectedCustomers = customers.filter(
      (customer) => customer !== this.args.primaryCustomer && customer !== this.args.secondaryCustomer,
    );
    analyticsData.matchCount = customersWithoutSelectedCustomers.length;

    this.analytics.track('people', 'merge_person_searched', analyticsData);
    this.customers = customersWithoutSelectedCustomers.slice(0, CUTOFF);
  });

  @action
  setDefaults() {
    this.isSelectingCustomer = this.selectedCustomer === null;
  }

  @action
  clearSearch() {
    this.customers = [];
    this.searchTask.cancelAll({ resetState: true });
  }

  @action
  selectCustomer(customer: CustomerModel) {
    this.isSelectingCustomer = false;
    this.analytics.track('people', 'merge_person_selected', { profileType: this.args.profileType });
    this.args.onSelect(customer);
  }

  @action
  selectAnotherCustomer() {
    this.analytics.track('people', 'merge_person_cleared', { profileType: this.args.profileType });
    this.args.onSelect(null);
    this.isSelectingCustomer = true;
  }

  @action
  onCustomersChange() {
    // Update the customers list, to prevent selecting the same customer for
    // both primary and secondary profiles
    if (this.customers.length > 0) {
      this.customers = this.customers.filter(
        (customer) => customer !== this.args.primaryCustomer && customer !== this.args.secondaryCustomer,
      );
    }
  }
}
